import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "shared/UI/Buttons/Button";
import { Checkbox, CheckBoxPosition } from "shared/UI/Checkbox/Checkbox";
import { PhoneNumberInput, PhoneNumberInputValue } from "shared/UI/PhoneNumberInput/PhoneNumberInput";
import { useUserContext } from "core/state/UserContext/UserContext";
import { playErrorHaptic } from "core/javascriptChannels";
import { BottomSheet } from "UIPalette/BottomSheet/BottomSheet";
import { getSavedPhoneCountry } from "../../Account/Components/PersonalInfoForm/PersonalInfoForm";
import OTPInput, { OTP_LENGTH } from "UIPalette/OTPInput/OTPInput";

type OnboardingStep = "confidentiality" | "phoneNumber" | "verify" | "allSet";

type StepContent = {
  title: string;
  header?: ReactNode;
  footer: ReactNode;
  body: ReactNode;
};

const RESEND_COUNTDOWN_S = 60;
const PRIVACY_POLICY_URL = "https://www.wisdomwellbeing.com/privacy-policy/";

const maskPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  return `${digits.slice(0, 2)}${"*".repeat(Math.max(digits.length - 5, 0))}${digits.slice(-3)}`;
};

export const AiBadge = () => (
  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-purple text-2xl font-bold text-white">Ai</div>
);

type WisdomAIOnboardingProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const WisdomAIOnboarding = ({ isOpen, onClose }: WisdomAIOnboardingProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "wisdomAI.onboarding" });
  const { t: tPhone } = useTranslation(undefined, { keyPrefix: "phoneNumberInput" });
  const { user } = useUserContext();
  const isMobileApp = !!window.flutter_inappwebview;
  const [step, setStep] = useState<OnboardingStep>("confidentiality");
  const [phone, setPhone] = useState<PhoneNumberInputValue>();
  const [hasPhoneError, setHasPhoneError] = useState(false);
  const [failedSubmitCount, setFailedSubmitCount] = useState(0);
  const [allowVoicemail, setAllowVoicemail] = useState(false);
  const [allowSms, setAllowSms] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(RESEND_COUNTDOWN_S);

  useEffect(() => {
    if (isOpen) {
      setStep("confidentiality");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== "verify" || resendIn <= 0) {
      return;
    }
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [isOpen, step, resendIn]);

  const goToVerify = () => {
    setOtp("");
    setResendIn(RESEND_COUNTDOWN_S);
    setStep("verify");
  };

  const submitPhoneNumber = () => {
    if (!phone?.isValid) {
      setHasPhoneError(true);
      setFailedSubmitCount((count) => count + 1);
      return;
    }
    setHasPhoneError(false);
    goToVerify();
  };

  const showPhoneError = hasPhoneError && !phone?.isValid;

  const steps: Record<OnboardingStep, StepContent> = {
    confidentiality: {
      title: t("title"),
      header: <AiBadge />,
      footer: (
        <>
          <Button text="Continue" onClick={() => setStep("phoneNumber")} />
          <Button variant="text" text="Back" onClick={onClose} />
        </>
      ),
      body: (
        <div className="space-y-4">
          <p>Before you begin, it's important that you understand how your information will be handled.</p>
          <p>
            Your conversation with this AI assistant is confidential and will be stored securely. Conversation transcripts will be reviewed and
            summarised using AI-assisted tools to support quality assurance, clinical governance, service improvement and safety monitoring. Your
            information will not be used to train AI models.
          </p>
          <p>
            Access to your information is restricted to authorised Wisdom Wellbeing staff, including mental health professionals involved in the
            delivery and oversight of the service.
          </p>
          <p>
            If we believe you or someone else is at risk of serious harm, we may have a legal, ethical or professional duty to share relevant
            information with appropriate services. This could include your GP, emergency services, social services, your employer or the police.
          </p>
          <p>
            Wherever possible, we will discuss any disclosure with you first, unless doing so would increase the risk of harm to you or someone else.
          </p>
          <p>
            For more information about how we collect, use and protect your personal information, please see our Privacy Policy:{" "}
            <a className="underline" href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
              {PRIVACY_POLICY_URL}
            </a>
          </p>
          <p>By continuing, you acknowledge that you have read and understood this information.</p>
        </div>
      ),
    },
    phoneNumber: {
      title: "What's your number?",
      footer: (
        <>
          <Button text="Continue" onClick={submitPhoneNumber} />
          <Button variant="text" text="Back" onClick={() => setStep("confidentiality")} />
        </>
      ),
      body: (
        <div className="space-y-4">
          <p>
            If a conversation suggests you could be in danger, a trained counsellor will call to check in and support you. As a result, we require
            your phone number for you to continue.
          </p>
          <p>
            Your phone number will only ever be used if a counsellor needs to call you, and will remain confidential in all other cases. It will never
            be used for marketing purposes. You can remove your phone number at any time on the <strong className="underline">permissions</strong>{" "}
            page.
          </p>
          <div className="space-y-1">
            <PhoneNumberInput
              countrySelectorLabel={tPhone("countrySelectorLabel")}
              numberInputLabel={tPhone("numberInputLabel")}
              placeholder={tPhone("placeholder")}
              defaultValue={phone ? phone.number : user?.phoneNumber}
              defaultCountry={phone ? phone.country : getSavedPhoneCountry(user)}
              hasError={showPhoneError}
              failedSubmitCount={failedSubmitCount}
              onErrorReset={() => setHasPhoneError(false)}
              onErrorHaptic={isMobileApp ? playErrorHaptic : undefined}
              onChange={setPhone}
            />
          </div>
          <Checkbox
            label="Can we leave you a voicemail?"
            position={CheckBoxPosition.Right}
            checked={allowVoicemail}
            onChange={(e) => setAllowVoicemail(e.target.checked)}
          />
          <Checkbox
            label="Can we contact you via SMS?"
            position={CheckBoxPosition.Right}
            checked={allowSms}
            onChange={(e) => setAllowSms(e.target.checked)}
          />
        </div>
      ),
    },
    verify: {
      title: "Verify it's you",
      footer: (
        <>
          <Button text="Confirm" disabled={otp.length !== OTP_LENGTH} onClick={() => setStep("allSet")} />
          <Button variant="text" text="Back" onClick={() => setStep("phoneNumber")} />
        </>
      ),
      body: (
        <div className="space-y-6 text-center">
          <p>We've sent a six digit code to {maskPhoneNumber(phone?.number ?? "")}.</p>
          <OTPInput handleChange={setOtp} />
          {resendIn > 0 ? (
            <p>
              Code sent. Send again in <strong>{resendIn}</strong>
            </p>
          ) : (
            <Button variant="text" text="Send again" onClick={goToVerify} />
          )}
        </div>
      ),
    },
    allSet: {
      title: "All set!",
      footer: <Button text="Start chatting" onClick={onClose} />,
      body: <p className="text-center">Your number's saved, so support is always close by. Let's talk about whatever's on your mind.</p>,
    },
  };

  const current = steps[step];

  return (
    <BottomSheet isOpen={isOpen} dismissible={false} title={current.title} header={current.header} footer={current.footer}>
      {current.body}
    </BottomSheet>
  );
};
